import {
    useRef,
    useState,
    useLayoutEffect,
    useEffect,
    useCallback,
    useMemo,
    RefObject,
} from 'react';

type AspectRatio = string | false; // 1/1, 16/9, 4/3, etc.

interface UseAspectRatioMinHeightOptions {
    /**
     * 傳一個外部 ref 進來
     */
    externalRef?: RefObject<HTMLElement>;
    /**
     * 當 minHeight 改變多少才更新（單位 px）
     */
    updateThreshold?: number;
    /**
     * 是否啟用
     */
    enable?: boolean;
}

// 確保不會在 SSR 出現警告
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function useAspectRatioMinHeight(
    aspectRatio?: AspectRatio,
    options?: UseAspectRatioMinHeightOptions
) {
    const [hydrated, setHydrated] = useState(false);

    const { externalRef, updateThreshold = 1, enable = true } = options || {};

    const observerRef = useRef<ResizeObserver | null>(null);

    const internalRef = useRef<HTMLElement | null>(null);

    const targetRef = externalRef ?? internalRef;

    const [minHeight, setMinHeight] = useState<number>();

    const updateHeight = useCallback(() => {
        if (!aspectRatio || !targetRef.current) {
            setMinHeight(undefined);

            return;
        }

        const parts = aspectRatio.split('/');

        if (parts.length !== 2) {
            return;
        }

        const width = Number(parts[0]);

        const height = Number(parts[1]);

        if (!width || !height || Number.isNaN(width) || Number.isNaN(height)) {
            setMinHeight(undefined);

            return;
        }

        const ratio = height / width;

        const containerWidth = targetRef.current.offsetWidth;

        const nextHeight = containerWidth * ratio;

        setMinHeight((prev) => {
            if (typeof prev !== 'number') {
                return nextHeight;
            }

            return Math.abs(prev - nextHeight) > updateThreshold ? nextHeight : prev;
        });
    }, [aspectRatio, targetRef, updateThreshold]);

    useEffect(() => {
        if (!enable) {
            setMinHeight(undefined);
        }
    }, [enable]);

    useEffect(() => {
        setHydrated(true);
    }, []);

    useIsomorphicLayoutEffect(() => {
        if (!targetRef.current || typeof window === 'undefined') {
            return () => {};
        }

        if (!enable) {
            return () => {};
        }

        let frameId: number | null = null;

        observerRef.current = new ResizeObserver(() => {
            if (frameId) {
                cancelAnimationFrame(frameId);
            }

            frameId = requestAnimationFrame(updateHeight);
        });

        observerRef.current.observe(targetRef.current);

        updateHeight();

        return () => {
            if (frameId) {
                cancelAnimationFrame(frameId);
            }

            observerRef.current?.disconnect();
        };
    }, [updateHeight, targetRef, enable]);

    return useMemo(
        () => ({
            ref: targetRef,
            minHeight,
            aspectRatio: !hydrated ? aspectRatio : undefined,
        }),
        [targetRef, minHeight, aspectRatio, hydrated]
    );
}
